// MarketHubCard — generic editorial write-up for employers in UAE,
// Singapore, Hong Kong, and China. Replaces the UAE-only
// UaeHubCard — same visual contract, but picks the right firm-hubs
// endpoint based on the employer's country.
//
// For UAE / SG / HK / CN the backend serves a `/{cc}/firm-hubs`
// endpoint that normalises `top_feeder_unis` to the dict shape
// `{id, signal, source_url, note}`. If the firm is missing from
// the hub module for its country, this component renders nothing
// (keeps the detail page clean when the slug came through from a
// different data pipeline).
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ExternalLink,
  Building2,
  MapPin,
  GraduationCap,
  Handshake,
  FileText,
  Newspaper,
  Linkedin,
  Landmark,
  BookOpen,
  Info,
  Building,
} from "lucide-react";
import { api } from "@/lib/api";

// Country → config. If a country isn't in this map, the card
// renders nothing (fail-closed). Each entry decides which API to
// hit, what eyebrow label + accent colour to use, and the headline
// framing ("UAE pipeline" vs "Singapore pipeline").
const MARKET_CONFIG = {
  AE: {
    endpoint: "/uae/firm-hubs",
    eyebrow: "UAE firm hub · editorial",
    headlineSuffix: "UAE graduate pipeline",
    accent: "amber-900",
    accentBg: "bg-amber-900",
    accentText: "text-amber-900/70",
    cta: "border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100",
    mapIcon: "text-amber-800",
  },
  SG: {
    endpoint: "/sg/firm-hubs",
    eyebrow: "Singapore firm hub · editorial",
    headlineSuffix: "Singapore graduate pipeline",
    accent: "red-900",
    accentBg: "bg-red-800",
    accentText: "text-red-800/70",
    cta: "border-red-400 bg-red-50 text-red-800 hover:bg-red-100",
    mapIcon: "text-red-700",
  },
  HK: {
    endpoint: "/hk/firm-hubs",
    eyebrow: "Hong Kong firm hub · editorial",
    headlineSuffix: "Hong Kong graduate pipeline",
    accent: "rose-900",
    accentBg: "bg-rose-900",
    accentText: "text-rose-900/70",
    cta: "border-rose-400 bg-rose-50 text-rose-900 hover:bg-rose-100",
    mapIcon: "text-rose-800",
  },
  CN: {
    endpoint: "/cn/firm-hubs",
    eyebrow: "China firm hub · editorial",
    headlineSuffix: "China graduate pipeline",
    accent: "red-900",
    accentBg: "bg-red-900",
    accentText: "text-red-900/70",
    cta: "border-red-400 bg-red-50 text-red-900 hover:bg-red-100",
    mapIcon: "text-red-800",
  },
};

// signal → {label, Icon, tone}. Covers the full palette across
// markets: UAE's `emiratisation` + SG/HK/CN's `government_scheme`
// (same semantic — public-sector talent pipeline) share the civic
// icon + rose tone so the UI feels consistent.
const SIGNAL_META = {
  strategic_partner: {
    label: "Strategic partner",
    Icon: Handshake,
    tone: "border-emerald-300 bg-emerald-50 text-emerald-800",
  },
  grad_scheme: {
    label: "Named on grad page",
    Icon: GraduationCap,
    tone: "border-sky-300 bg-sky-50 text-sky-800",
  },
  annual_report: {
    label: "Annual report",
    Icon: FileText,
    tone: "border-indigo-300 bg-indigo-50 text-indigo-800",
  },
  press: {
    label: "Press / award",
    Icon: Newspaper,
    tone: "border-amber-300 bg-amber-50 text-amber-900",
  },
  linkedin_alumni: {
    label: "LinkedIn alumni",
    Icon: Linkedin,
    tone: "border-blue-300 bg-blue-50 text-blue-800",
  },
  emiratisation: {
    label: "Emiratisation filing",
    Icon: Landmark,
    tone: "border-rose-300 bg-rose-50 text-rose-800",
  },
  government_scheme: {
    label: "Government scheme",
    Icon: Landmark,
    tone: "border-rose-300 bg-rose-50 text-rose-800",
  },
  editorial: {
    label: "Editorial pick",
    Icon: BookOpen,
    tone: "border-stone-300 bg-stone-50 text-stone-600",
  },
};

function FeederChip({ feeder }) {
  const id = typeof feeder === "string" ? feeder : feeder?.id;
  const signal = typeof feeder === "string" ? "editorial" : (feeder?.signal || "editorial");
  const sourceUrl = typeof feeder === "string" ? null : feeder?.source_url;
  const note = typeof feeder === "string" ? null : feeder?.note;
  if (!id) return null;
  const meta = SIGNAL_META[signal] || SIGNAL_META.editorial;
  const { Icon } = meta;
  const tooltip = [meta.label, note].filter(Boolean).join(" · ");
  return (
    <div
      className="inline-flex items-stretch rounded-full border border-stone-200 bg-stone-50 overflow-hidden"
      data-testid={`market-hub-feeder-${id}`}
    >
      <Link
        to={`/uni/${id}`}
        className="px-3 py-1 text-xs text-stone-700 hover:text-[#4C1D95] hover:bg-white transition-colors"
      >
        {/* Strip the country prefix and dash-decorate the slug tail */}
        {id.replace(/^(ae|sg|hk|cn)-/, "").replace(/-/g, " ")}
      </Link>
      {sourceUrl ? (
        <a
          href={sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={tooltip}
          data-testid={`market-hub-feeder-${id}-source`}
          className={`inline-flex items-center gap-1 border-l px-2 py-1 text-[10px] font-medium ${meta.tone} hover:opacity-80`}
        >
          <Icon className="w-3 h-3" /> {meta.label}
        </a>
      ) : (
        <span
          title={tooltip}
          data-testid={`market-hub-feeder-${id}-signal`}
          className={`inline-flex items-center gap-1 border-l px-2 py-1 text-[10px] font-medium ${meta.tone}`}
        >
          <Icon className="w-3 h-3" /> {meta.label}
        </span>
      )}
    </div>
  );
}

export default function MarketHubCard({ firmName, country }) {
  const [hub, setHub] = useState(null);
  const cfg = MARKET_CONFIG[country];

  useEffect(() => {
    if (!cfg || !firmName) { setHub(null); return; }
    let cancelled = false;
    api.get(cfg.endpoint)
      .then(({ data }) => {
        if (cancelled) return;
        const firms = data?.firms || {};
        let hit = firms[firmName];
        if (!hit) {
          const needle = (firmName || "").toLowerCase();
          const match = Object.entries(firms).find(
            ([k]) => k.toLowerCase() === needle
          );
          if (match) hit = match[1];
        }
        setHub(hit || null);
      })
      .catch(() => setHub(null));
    return () => { cancelled = true; };
  }, [firmName, country, cfg]);

  if (!cfg || !hub) return null;

  const feeders = Array.isArray(hub.top_feeder_unis) ? hub.top_feeder_unis : [];
  const sourced = feeders.filter(
    (f) => f && typeof f === "object" && f.signal && f.signal !== "editorial"
  ).length;
  const total = feeders.length;

  return (
    <div
      data-testid="market-hub-card"
      data-country={country}
      className="mt-6 rounded-xl border border-stone-200 bg-white p-5 sm:p-6"
    >
      <div className="flex items-start gap-3">
        <div className={`shrink-0 mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full text-stone-50 ${cfg.accentBg}`}>
          <Building2 className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] font-underground-sc tracking-[0.18em] ${cfg.accentText}`}>
            {cfg.eyebrow}
          </div>
          <h3 className="mt-1 font-editorial text-xl text-stone-900 leading-tight">
            How {firmName} runs its {cfg.headlineSuffix}
          </h3>
          <p className="mt-3 text-sm text-stone-700 leading-relaxed max-w-3xl">
            {hub.hub}
          </p>

          <div className="mt-4 flex flex-wrap gap-3 text-sm">
            {hub.location && (
              <span className="inline-flex items-center gap-1.5 text-stone-600">
                <MapPin className={`w-4 h-4 ${cfg.mapIcon}`} /> {hub.location}
              </span>
            )}
            {hub.grad_url && (
              <a
                href={hub.grad_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="market-hub-grad-url"
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${cfg.cta}`}
              >
                <ExternalLink className="w-3 h-3" /> Visit grad careers page
              </a>
            )}
          </div>

          {feeders.length > 0 && (
            <div className="mt-5">
              <div className="label-uppercase text-stone-600 mb-2 flex items-center gap-1.5">
                <GraduationCap className="w-3 h-3" /> Top feeder unis
                <span
                  className="ml-2 inline-flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-stone-500"
                  data-testid="market-hub-evidence-summary"
                >
                  <Info className="w-3 h-3" />
                  {sourced}/{total} with a public source — the rest are
                  editorial picks
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {feeders.map((f, idx) => (
                  <FeederChip key={(typeof f === "string" ? f : f?.id) || idx} feeder={f} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
